import React, { useEffect, useState } from "react";
import {
  Layout,
  Row,
  Col,
  Card,
  Typography,
  Avatar,
  Tag,
  List,
  Button,
  Empty,
  Space,
  message,
} from "antd";
import {
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PieChartOutlined,
  BarChartOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import CountUp from "react-countup";
import axios from "axios";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Components/Sidebar";
import { BASE_URL } from "../Config";

const { Header, Content } = Layout;
const { Title, Paragraph, Text } = Typography;

const CHART_COLORS = ["#4f46e5", "#06b6d4", "#f59e0b", "#ef4444"];

const Dashboard = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const [loading, setLoading] = useState(false);

  const [stats, setStats] = useState({
    totalVariations: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  });

  const [lineData, setLineData] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);

      const token = sessionStorage.getItem("token");

      const countRes = await axios.get(`${BASE_URL}/dashboard/counts`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setStats(countRes.data.data);

      const monthRes = await axios.get(`${BASE_URL}/dashboard/charts/monthly`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLineData(monthRes.data.data);

      const statusRes = await axios.get(`${BASE_URL}/dashboard/charts/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setPieData(
        statusRes.data.data.map((item) => ({
          name: item._id,
          value: item.count,
        })),
      );

      const pendingRes = await axios.get(`${BASE_URL}/userasanas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPendingApprovals(
        pendingRes.data.asanas.filter((x) => x.status === "pending"),
      );

      const recentRes = await axios.get(`${BASE_URL}/dashboard/recent`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setRecentActivities(
        recentRes.data.data.map((item) => ({
          _id: item._id,
          title: `${item.name} submitted`,
          by: item.userId?.name || "User",
          at: new Date(item.createdAt).toLocaleString(),
          status: item.status,
        })),
      );
    } catch (err) {
      console.error("Dashboard fetch error", err);
      message.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const auth = {
    headers: { Authorization: `Bearer ${sessionStorage.getItem("token")}` },
  };

  const approve = async (id) => {
    await axios.put(`${BASE_URL}/userasanas/approve/${id}`, {}, auth);
    message.success("Approved");
    fetchDashboard();
  };

  const reject = async (id) => {
    await axios.put(`${BASE_URL}/userasanas/reject/${id}`, {}, auth);
    message.success("Rejected");
    fetchDashboard();
  };

  const StatCard = ({ title, value, icon, color }) => (
    <Card style={{ borderRadius: 12, minHeight: 110, background: "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          <Text type="secondary">{title}</Text>
          <div style={{ display: "flex", alignItems: "center", marginTop: 8 }}>
            <Title level={3} style={{ margin: 0 }}>
              <CountUp end={value} duration={1.2} />
            </Title>
            <Avatar
              style={{
                background: color,
                marginLeft: 12,
                boxShadow: "0 6px 14px rgba(0,0,0,0.06)",
              }}
              icon={icon}
            />
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <Layout style={{ minHeight: "100vh", background: "#f5f7fa" }}>
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        isMobile={isMobile}
        collapsedWidth="0"
        role="admin"
      />
      <Layout
        className="main-layout"
        style={{
          marginLeft: isMobile ? 0 : collapsed ? 0 : 240,
          transition: "0.3s",
          padding: isMobile ? 10 : 20,
        }}
      >
        <Header
          className="MainHeader"
        >
          {isMobile && (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setMobileOpen(true)}
              style={{ fontSize: 18, marginRight: 12 }}
            />
          )}
          <div style={{ flex: 1 }}>
            <Title level={4}>Yoga Wellness</Title>
            <Text type="secondary">Admin dashboard</Text>
          </div>
        </Header>

        <Content style={{ padding: 24 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <StatCard
                title="Total Variations"
                value={stats.totalVariations}
                icon={<BarChartOutlined />}
                color="#4f46e5"
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <StatCard
                title="Pending"
                value={stats.pending}
                icon={<PieChartOutlined />}
                color="#f59e0b"
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <StatCard
                title="Approved"
                value={stats.approved}
                icon={<CheckCircleOutlined />}
                color="#10b981"
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <StatCard
                title="Rejected"
                value={stats.rejected}
                icon={<CloseCircleOutlined />}
                color="#ef4444"
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 18 }}>
            <Col xs={24} lg={16}>
              <Card style={{ borderRadius: 12 }}>
                <Title level={5}>Reports</Title>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={lineData}>
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#4f46e5"
                      strokeWidth={3}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </Col>

            <Col xs={24} lg={8}>
              <Card style={{ borderRadius: 12 }}>
                <Title level={5}>Submission Status</Title>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={"60%"}
                      outerRadius={"80%"}
                    >
                      {pieData.map((p, i) => (
                        <Cell key={i} fill={CHART_COLORS[i]} />
                      ))}
                    </Pie>
                    <Legend />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>

          {/* Approvals + Recent */}
          <Row gutter={[16, 16]} style={{ marginTop: 18 }}>
            <Col xs={24} lg={12}>
              <Card style={{ borderRadius: 12 }}>
                <Title level={5}>Pending Approvals</Title>

                {pendingApprovals?.length === 0 ? (
                  <Empty description="No Pending Approvals" />
                ) : (
                  <List
                    dataSource={pendingApprovals}
                    renderItem={(item) => (
                      <List.Item
                        actions={[
                          <Button
                            type="primary"
                            onClick={() => approve(item._id)}
                          >
                            Approve
                          </Button>,
                          <Button danger onClick={() => reject(item._id)}>
                            Reject
                          </Button>,
                        ]}
                      >
                        <List.Item.Meta
                          avatar={<Avatar icon={<UserOutlined />} />}
                          title={item.name}
                          description={`By ${item.userId?.name || "User"
                            } • ${new Date(item.createdAt).toLocaleString()}`}
                        />
                      </List.Item>
                    )}
                  />
                )}
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card style={{ borderRadius: 12 }}>
                <Title level={5}>Recent Activities</Title>

                <List
                  dataSource={recentActivities}
                  renderItem={(it) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={
                          <Avatar
                            style={{ background: "#eef2ff", color: "#4f46e5" }}
                          >
                            {it.by?.[0]}
                          </Avatar>
                        }
                        title={
                          <>
                            {it.title}
                            <Tag
                              color={
                                it.status === "approved"
                                  ? "green"
                                  : it.status === "pending"
                                    ? "orange"
                                    : "red"
                              }
                              style={{ marginLeft: 10 }}
                            >
                              {it.status}
                            </Tag>
                          </>
                        }
                        description={`${it.by} • ${it.at}`}
                      />
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
          </Row>
        </Content>
      </Layout>
    </Layout>
  );
};

export default Dashboard;
